/**
 * Pure renderer for a Talking Heads canvas design at any pixel size.
 * Mirrors helper/talking_heads_canvas.py so the studio preview is what the
 * final video looks like: background → tint → pattern → vignette →
 * "under" decor → frame shadows → video boxes (rounded / circle clipped) →
 * frame strokes → "over" decor.
 */
import React, { useId } from 'react';
import { MdFaceRetouchingNatural, MdMovie, MdVideoLibrary } from 'react-icons/md';
import { LayerView, hexToRgba } from './bannerScene';
import {
  cornerRadius,
  decorFor,
  frameStrokes,
  modeLayerKeys,
} from './thCanvas';

export function backgroundCss(bg) {
  if (bg.type === 'solid') return bg.color;
  if (bg.type === 'gradient') {
    const g = bg.gradient;
    return g.type === 'radial'
      ? `radial-gradient(ellipse farthest-corner at 50% 50%, ${g.from}, ${g.to})`
      : `linear-gradient(${g.angle}deg, ${g.from}, ${g.to})`;
  }
  return '#0b0b12';
}

const PLACEHOLDER = {
  content: { bg: 'linear-gradient(135deg, #12324a 0%, #0b1a2b 55%, #142a1c 100%)', icon: MdVideoLibrary, label: 'Narration footage' },
  avatar: { bg: 'radial-gradient(ellipse at 50% 35%, #4c3a8f 0%, #1e1640 70%)', icon: MdFaceRetouchingNatural, label: 'Avatar' },
  clip: { bg: 'linear-gradient(135deg, #0c3b5e 0%, #0a1f33 100%)', icon: MdMovie, label: 'Clip' },
};

function Placeholder({ kind, w, h, labels }) {
  const p = PLACEHOLDER[kind];
  const Icon = p.icon;
  const size = Math.max(10, Math.min(w, h) * 0.22);
  return (
    <div className="thc-ph" style={{ background: p.bg }}>
      <Icon style={{ fontSize: size, opacity: 0.55 }} aria-hidden="true" />
      {labels && w > 90 && h > 50 && <span style={{ fontSize: Math.max(9, Math.min(13, h * 0.08)) }}>{p.label}</span>}
    </div>
  );
}

function fitCss(fit) {
  if (fit === 'contain') return 'contain';
  if (fit === 'fill') return 'fill';
  return 'cover';
}

function Media({ kind, media, fit, w, h, labels }) {
  if (kind === 'avatar' && media?.avatarUrl) {
    // backend: avatar contained in its box over a blurred cover copy of itself
    return (
      <>
        <video className="thc-media thc-media--blur" src={media.avatarUrl} muted playsInline autoPlay loop preload="auto" aria-hidden="true" />
        <video
          className="thc-media"
          src={media.avatarUrl}
          muted
          playsInline
          autoPlay
          loop
          preload="auto"
          style={{ objectFit: 'contain' }}
          onLoadedMetadata={media.onAvatarMeta}
        />
      </>
    );
  }
  const sample = kind === 'content' ? media?.contentSample : kind === 'clip' ? (media?.clipSample || media?.contentSample) : null;
  if (sample) {
    // renderer: "contain" fills the empty sides with a blurred copy of the same video
    if (fit === 'contain') {
      return (
        <>
          <img className="thc-media thc-media--blur" src={sample} alt="" draggable={false} aria-hidden="true" />
          <img className="thc-media" src={sample} alt="" draggable={false} style={{ objectFit: 'contain' }} />
        </>
      );
    }
    return <img className="thc-media" src={sample} alt="" draggable={false} style={{ objectFit: fitCss(fit) }} />;
  }
  return <Placeholder kind={kind} w={w} h={h} labels={labels} />;
}

/** SVG pattern identical to _pattern_layer() (diagonal uses the CSS gradient). */
function PatternLayer({ pattern, W, H, dims, uid }) {
  if (!pattern || pattern.kind === 'none' || !(pattern.opacity > 0)) return null;
  const { kind, color, opacity, size, thickness: t } = pattern;
  if (kind === 'diagonal') {
    const s = dims.w / W;
    const c = hexToRgba(color, opacity);
    return (
      <div
        className="thc-fill"
        style={{ background: `repeating-linear-gradient(45deg, ${c} 0px, ${c} ${t * s}px, transparent ${t * s}px, transparent ${size * s}px)` }}
      />
    );
  }
  const id = `thc-pat-${uid}`;
  let shape = null;
  if (kind === 'grid') shape = <path d={`M0 0H${size}V${t}H0Z M0 0H${t}V${size}H0Z`} fill={color} />;
  else if (kind === 'scanlines') shape = <rect x="0" y="0" width={size} height={t} fill={color} />;
  else if (kind === 'dots') shape = <circle cx={size / 2} cy={size / 2} r={t * 1.5} fill={color} />;
  if (!shape) return null;
  return (
    <svg className="thc-fill" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <pattern id={id} width={size} height={size} patternUnits="userSpaceOnUse">{shape}</pattern>
      </defs>
      <rect width={W} height={H} fill={`url(#${id})`} opacity={opacity} />
    </svg>
  );
}

function FrameStrokes({ layer }) {
  const f = layer.frame;
  if (!f || !(f.width > 0) || !(f.opacity > 0)) return null;
  const { x, y, width: w, height: h } = layer;
  const R = cornerRadius(f, w, h);
  return frameStrokes(f, w, h).map((st, i) => {
    const cw = w - 2 * st.inset;
    const ch = h - 2 * st.inset;
    if (cw <= 0 || ch <= 0) return null;
    const common = {
      fill: 'none',
      stroke: f.color,
      strokeOpacity: f.opacity,
      strokeWidth: st.width,
      strokeDasharray: st.dash ? `${st.dash[0]} ${st.dash[1]}` : undefined,
      strokeLinecap: st.dots ? 'round' : 'butt',
    };
    if (f.shape === 'circle') {
      return <ellipse key={i} cx={x + w / 2} cy={y + h / 2} rx={cw / 2} ry={ch / 2} {...common} />;
    }
    const r = Math.max(0, R - st.inset);
    return <rect key={i} x={x + st.inset} y={y + st.inset} width={cw} height={ch} rx={r} ry={r} {...common} />;
  });
}

/**
 * design  — normalized design (thCanvas.normalizeDesign)
 * mode    — 'narration' | 'clip'
 * W, H    — output canvas px; dims — preview px
 * media   — { avatarUrl, contentSample, clipSample, bgPreview, onAvatarMeta }
 */
export default function ThCanvasScene({
  design,
  mode = 'narration',
  W,
  H,
  dims,
  media = null,
  labels = true,
  animate = false,
  className = '',
}) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  if (!design || !dims?.w) return null;
  const s = dims.w / W;
  const bg = design.background;
  const keys = modeLayerKeys(design, mode);
  const px = (v) => v * s;

  // blurred background source: what the renderer blurs for this mode
  let blurSrc = null;
  if (bg.type === 'blurred') {
    if (mode === 'clip') blurSrc = media?.clipSample || media?.contentSample ? { img: media.clipSample || media.contentSample } : { kind: 'clip' };
    else if (design.layoutKind === 'full_avatar') blurSrc = media?.avatarUrl ? { video: media.avatarUrl } : { kind: 'avatar' };
    else blurSrc = media?.contentSample ? { img: media.contentSample } : { kind: 'content' };
  }
  const blurPx = Math.max(2, 6 * s);

  return (
    <div className={`thc-scene ${animate ? 'thc-scene--animate' : ''} ${className}`} style={{ width: dims.w, height: dims.h }}>
      {/* background */}
      <div className="thc-fill" style={{ background: backgroundCss(bg) }} />
      {blurSrc && (
        <div className="thc-fill thc-blurbg" style={{ filter: `blur(${blurPx}px)` }}>
          {blurSrc.img && <img src={blurSrc.img} alt="" draggable={false} />}
          {blurSrc.video && <video src={blurSrc.video} muted playsInline autoPlay loop aria-hidden="true" />}
          {blurSrc.kind && <div className="thc-fill" style={{ background: PLACEHOLDER[blurSrc.kind].bg }} />}
        </div>
      )}
      {(bg.type === 'image' || bg.type === 'video') && (media?.bgPreview || bg.url) && (
        bg.type === 'video'
          ? <video className="thc-fill thc-cover" src={media?.bgPreview || bg.url} muted playsInline autoPlay loop aria-hidden="true" />
          : <img className="thc-fill thc-cover" src={media?.bgPreview || bg.url} alt="" draggable={false} />
      )}
      {['blurred', 'image', 'video'].includes(bg.type) && bg.tint?.opacity > 0 && (
        <div className="thc-fill" style={{ background: hexToRgba(bg.tint.color, bg.tint.opacity) }} />
      )}
      <PatternLayer pattern={bg.pattern} W={W} H={H} dims={dims} uid={uid} />
      {bg.vignette > 0 && (
        <div
          className="thc-fill"
          style={{ background: `radial-gradient(ellipse farthest-corner at 50% 50%, rgba(0,0,0,0) 55%, rgba(0,0,0,${bg.vignette}) 100%)` }}
        />
      )}

      {/* under decor */}
      {decorFor(design, 'under', mode).map((l) => <LayerView key={l.id} layer={l} dims={dims} scale={s} />)}

      {/* shadows — all beneath every box, like the UNDER png */}
      {keys.map((k) => {
        const l = design.layers[k];
        const sh = l.shadow;
        if (!sh?.enabled || !(sh.opacity > 0)) return null;
        const R = cornerRadius(l.frame, l.width, l.height);
        return (
          <div
            key={`sh-${k}`}
            className="thc-box"
            style={{
              left: px(l.x), top: px(l.y), width: px(l.width), height: px(l.height),
              borderRadius: l.frame.shape === 'circle' ? '50%' : px(R),
              boxShadow: `${px(sh.x)}px ${px(sh.y)}px ${px(sh.blur)}px ${px(sh.spread)}px ${hexToRgba(sh.color, sh.opacity)}`,
            }}
          />
        );
      })}

      {/* video boxes */}
      {keys.map((k) => {
        const l = design.layers[k];
        const R = cornerRadius(l.frame, l.width, l.height);
        const fit = k === 'content' ? design.contentFit : k === 'clip' ? design.clipFit : 'contain';
        return (
          <div
            key={k}
            className={`thc-box thc-box--${k}`}
            style={{
              left: px(l.x), top: px(l.y), width: px(l.width), height: px(l.height),
              borderRadius: l.frame.shape === 'circle' ? '50%' : px(R),
            }}
          >
            <Media kind={k} media={media} fit={fit} w={px(l.width)} h={px(l.height)} labels={labels} />
          </div>
        );
      })}

      {/* frame strokes */}
      <svg className="thc-fill thc-frames" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
        {keys.map((k) => <FrameStrokes key={k} layer={design.layers[k]} />)}
      </svg>

      {/* over decor */}
      {decorFor(design, 'over', mode).map((l) => <LayerView key={l.id} layer={l} dims={dims} scale={s} />)}
    </div>
  );
}
